<template lang="pug">
div
  v-row.mx-3.mb-0

    .caption {{ myTitle }}

    v-spacer

    v-tooltip(bottom)
      template(#activator="{ on, attrs }")
        v-btn(
          v-bind="attrs"
          v-on="on"
          x-small
          :disabled="!content"
          :text="!content"
          @click="copyToClipboard(indentedContent)"
        )
          v-icon(left) mdi-clipboard
          | Copiar
      span
        span.text-md.font-weight-medium Copiar
        br
        span.font-weight-light Copiar o XML para a área de transferência...

    v-tooltip(bottom)
      template(#activator="{ on, attrs }")
        v-btn(
          v-bind="attrs"
          v-on="on"
          x-small
          :disabled="!content"
          :text="!content"
          @click="downloadXml(indentedContent)"
        )
          v-icon(left) mdi-download-circle
          | Baixar
      span
        span.text-md.font-weight-medium Baixar
        br
        span.font-weight-light Baixar o XML como arquivo...

  //-
  //- APRESENTACAO DO TEXTO MARKDOWN
  //-
  markdown-viewer.mx-3.mb-0(
    :source="markdown"
    :height="myMultiline"
  )

  v-snackbar(
    v-model="notification.visible"
    :timeout="notification.timeout"
  )
    | {{ notification.message }}

    template(
      v-if="notification.timeout > 1000"
      #action="{ attrs }"
    )
      v-btn(
        text
        v-bind="attrs"
        @click="notification.visible = false"
        :color="notification.color"
      )
        | Fechar
</template>

<script>
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import { interpolate, isInterpolated } from '@/helpers/strings'
import MarkdownViewer from '@/components/commons/markdown-viewer/MarkdownViewer'
import xmlFormat from 'xml-formatter'

export default {
  components: {
    MarkdownViewer,
  },

  props: {
    /**
     * Objeto contendo os valores e campos do contexto no qual o widget está
     * inserido para inferência de propriedades aplicadas em interpolação de
     * templates de texto.
     */
    context: Object,

    /**
     * Instância opcional de um campo do Paper.
     * @type {Keep.Paper.Design.Field}
     */
    field: Object,

    /**
     * Um dos tipos de dados suportados pelo Paper.
     * @type {Keep.Paper.Design.TypeNames}
     */
    dataType: String,

    /**
     * Diz se o componente suporta seleção de intervalo.
     * @type {Boolean}
     */
    allowRange: Boolean,

    /**
     * Valor editado no componente.
     * @type {Any}
     */
    value: {
      validator: v => true
    },
  },

  data: () => ({
    icons: {
      mdiEyeOutline,
      mdiEyeOffOutline,
    },

    indentedContent: null,

    dataSet: null,  // Um objeto do tipo {Keep.Paper.Design.DataSet}
    failure: null,  // Um objeto do tipo {Keep.Paper.Design.Status}

    revealed: false,

    notification: {
      visible: false,
      message: 'Copiado para a área de trabalho.',
      timeout: 2000,
      color: 'success',
    },
  }),

  computed: {
    myName: vm => vm.getIt('name'),
    myTitle: vm => vm.getIt('title') || vm.myName,
    myDescription: vm => vm.getIt('description'),
    myHint: vm => vm.getIt('hint'),
    myPlaceholder: vm => vm.getIt('placeholder'),
    isSecure: vm => vm.hasIt('secure'),
    isLarge: vm => vm.hasIt('large'),
    hasNoHints: vm => vm.hasIt('noHints'),
    isRequired: vm => vm.hasIt('required'),
    isDisabled: vm => vm.hasIt('disabled'),
    isReadOnly: vm => vm.hasIt('readOnly') || vm.hasIt('readonly'),
    myIcon: vm => vm.getIt('icon'),
    myFont: vm => vm.getIt('font'),

    myMultiline: vm => vm.getIt('multiline'),
    isSelect: vm => vm.hasIt('dataSet'),

    myType () { return this.isSecure && !this.revealed ? 'password' : 'text' },
    myAutoComplete () {
      if (this.myName?.toLowerCase() === 'username') return 'username'
      if (this.myName?.toLowerCase() === 'password') return 'current-password'
      return null
    },

    errorMessages () {
      return this.failure
        ? [ 'Não foi possível carregar os dados.', this.failure.message ]
        : null
    },

    classes () {
      const classes = {}

      classes['x-widget-readonly'] = this.isReadOnly

      // FontStyle:
      //    Default = 0,
      //    Italic = 1,
      //    Bold = 2,
      //    Overline = 4,
      //    Strikethrough = 8,
      //    Underline = 16,
      //    Monospace = 32,
      const font = this.myFont || 0
      if ((font &  1) ==  1) classes['font-italic'] = true
      if ((font &  2) ==  2) classes['font-weight-bold'] = true
      if ((font &  4) ==  4) classes['text-decoration-overline'] = true
      if ((font &  8) ==  8) classes['text-decoration-line-through'] = true
      if ((font & 16) == 16) classes['text-decoration-underline'] = true
      if ((font & 32) == 32) classes['x-font-monospace'] = true

      return classes
    },

    metaIcon () {
      if (!this.isSecure) return null
      return this.revealed ? this.icons.mdiEyeOffOutline : this.icons.mdiEyeOutline
    },

    content: {
      get () {
        return this.value || ''
      },
      set (value) {
        this.$emit('input', value || null)
      }
    },

    markdown () {
      return `
\`\`\`xml
${this.indentedContent || ''}
\`\`\`
`
    },

    rules () {
      const required = (this.isRequired && !this.isDisabled)
      return [
        v => !required || !!v || 'Campo requerido',
        // v => (v && v.length <= 10) || 'Name must be less than 10 characters',
      ]
    },
  },

  watch: {
    $route () {
      this.loadOptionsAsync()
      this.indentContent()
    },
    content () {
      this.indentContent()
    },
  },

  mounted () {
    this.loadOptionsAsync()
    this.indentContent()
  },

  methods: {
    getIt (property) {
      return this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
    },
    hasIt (property) {
      if (this.$props[property] === '') return true
      if (this.$attrs[property] === '') return true
      const value = this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
      return !!value
    },

    async loadOptionsAsync () {
      this.dataSet = null
      this.failure = null

      var dataSet = this.getIt('dataSet')
      if (!dataSet?.see) {
        this.dataSet = dataSet
        return
      }

      // Resolvendo a referência ao dataset...
      const result = await this.$resolve(dataSet.see, { noCache: true })
      if (!result.ok) {
        this.failure = result.entity
        return
      }

      this.dataSet = result.entity
    },

    indentContent() {
      if (this.content) {
        const options = { indentation: '  ', collapseContent: true }
        this.$nextTick(function () {
          try {
            this.indentedContent = xmlFormat(this.content, options)
          } catch (e) {
            this.indentedContent = this.content
          }
        })
      } else {
        this.indentedContent = ''
      }
    },

    /*copyToClipboard (content) {
      navigator.clipboard.writeText(content);
      this.$set(this.notification, 'visible', true)
    },*/

    copyToClipboard(content) {
      const textarea = document.createElement('textarea');
      textarea.value = content;

      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);
      textarea.select();

      try {
        const ok = document.execCommand('copy');
        if (ok) {
          this.$set(this.notification, 'visible', true);
        } else {
          console.warn('Falha ao copiar');
        }
      } catch (err) {
        console.error('Erro ao copiar: ', err);
      }

      document.body.removeChild(textarea);
    },

    downloadXml (content) {
      const mimeType = 'text/xml'
      const filename = (this.field?.title || this.field?.name || 'Documento')+'.xml'
      this.download(content, mimeType, filename)
    },

    download (content, mimeType, filename) {
      const a = document.createElement('a')
      const blob = new Blob([content], {type: mimeType})
      const url = URL.createObjectURL(blob)
      a.setAttribute('href', url)
      a.setAttribute('download', filename)
      a.click()
    },

    format(text) {
      return isInterpolated(text) ? interpolate(text, this.context) : text
    },
  },
}
</script>